<template>
  <div
    ref="scrollbarRoot"
    class="custom-scrollbar-container"
    :class="{ 'custom-scroll-accent': !defaultStyle }"
  >
    <perfect-scrollbar
      ref="perfectScrollbarRef"
      :id="scrollerId"
      :class="scrollComponentClass"
      :options="settings"
      :tabindex="scrollLandmarkTabindex"
      :role="scrollLandmarkRole"
      :aria-label="scrollLandmarkAriaLabel"
      @keydown="onKeyboardScroll"
      @ps-scroll-y="emitEvent"
      @ps-scroll-x="emitEvent"
    >
      <slot></slot>
    </perfect-scrollbar>
  </div>
</template>

<script>
import { stripPerfectScrollbarThumbTabindex } from "@/helpers/stripPerfectScrollbarThumbTabindex.js"

export default {
  props: {
    settings: {
      type: Object,
      default: function () {
        return {}
      }
    },
    scrollComponentClass: [Array, String],
    scrollerId: String,
    defaultStyle: Boolean,
    /** When true, the scrollable region is in the tab order (arrow keys / keyboard access). */
    keyboardAccessible: {
      type: Boolean,
      default: false
    },
    /** When set, exposes role="region" and this string as aria-label on the scroll container. */
    scrollRegionLabel: {
      type: String,
      default: ""
    }
  },
  setup(props, { emit }) {
    const emitEvent = (event) => {
      emit(event.type, event)
    }

    return {
      emitEvent
    }
  },
  computed: {
    /** Landmark for the scroll area (Deque: programmatic role + name near scrollbar UI). */
    scrollLandmarkRole() {
      if (this.scrollRegionLabel) return "region"
      return undefined
    },
    scrollLandmarkAriaLabel() {
      return this.scrollRegionLabel || undefined
    },
    scrollLandmarkTabindex() {
      if (this.keyboardAccessible && this.scrollRegionLabel) return 0
      return undefined
    }
  },
  mounted() {
    const scrollbarRoot = this.$refs.scrollbarRoot
    if (!scrollbarRoot?.querySelectorAll) {
      this._psThumbObserver = null
      return
    }

    this.$nextTick(() => {
      stripPerfectScrollbarThumbTabindex(scrollbarRoot)
    })
    if (typeof MutationObserver !== "undefined") {
      this._psThumbObserver = new MutationObserver(() => {
        stripPerfectScrollbarThumbTabindex(scrollbarRoot)
      })
      if (scrollbarRoot) {
        this._psThumbObserver.observe(scrollbarRoot, {
          subtree: true,
          childList: true
        })
      }
    } else {
      this._psThumbObserver = null
    }
  },
  beforeUnmount() {
    this._psThumbObserver?.disconnect()
    this._psThumbObserver = null
  },
  methods: {
    onKeyboardScroll(event) {
      if (!this.keyboardAccessible || event?.target !== event?.currentTarget) {
        return
      }

      const scroller = event.currentTarget
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return

      const step = 40
      const pageStep = Math.max(step, Math.round(scroller.clientWidth * 0.8))
      const movements = {
        ArrowLeft: -step,
        ArrowRight: step,
        PageUp: -pageStep,
        PageDown: pageStep
      }

      if (event.key === "Home") {
        event.preventDefault()
        scroller.scrollLeft = 0
      } else if (event.key === "End") {
        event.preventDefault()
        scroller.scrollLeft = scroller.scrollWidth
      } else if (movements[event.key]) {
        event.preventDefault()
        scroller.scrollLeft += movements[event.key]
      }
    },
    updatePerfectScrollbar() {
      const cmp = this.$refs.perfectScrollbarRef
      if (!cmp) {
        return
      }
      const exposed = cmp.ps
      const ps =
        exposed &&
        typeof exposed === "object" &&
        "value" in exposed &&
        exposed.value &&
        typeof exposed.value.update === "function"
          ? exposed.value
          : exposed && typeof exposed.update === "function"
            ? exposed
            : null
      ps?.update()
    }
  }
}
</script>
